import { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import logo from "../../assets/images/Logo.png";
import { Calendar } from "lucide-react";

import useAuthStore from "../../store/useAuthStore"; // Import the Zustand store
import SERVER_URL from "../../config/server";

export default function Navbar() {
  const [showDropdown, setShowDropdown] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();
  const { isLoggedIn, username, checkAuth, email, logout, ProfilePicture } =
    useAuthStore();
  const ProfileImage = SERVER_URL + ProfilePicture;
  useEffect(() => {
    checkAuth(); // Re-check authentication on route change
  }, [location.pathname, checkAuth]);
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <nav className="border-gray-200 fixed top-0 left-0 w-full z-50 bg-transparent mb-10 sm:mb-2 md:mb-3 lg:mb-4 xl:mb-5 shadow-lg backdrop-blur-md">
      <div className="max-w-screen-xl flex flex-wrap items-center justify-between mx-auto p-2 sm:p-3 md:p-4 lg:p-5 xl:p-6">
        {/* Left Spacer for mobile centering */}
        <div className="flex-1 md:hidden" />

        {/* Logo */}
        <div className="flex justify-center md:justify-start md:flex-1">
          <div className="bg-white/5 dark:bg-black/10 border border-white/10 backdrop-blur-md rounded-xl px-3 py-2 shadow-lg transition-all duration-300 hover:shadow-cyan-500/20 hover:scale-[1.02] w-fit">
            <Link
              to="/"
              className="flex items-center gap-2 sm:gap-3 transition-all duration-300 group"
            >
              <img
                src={logo}
                alt="Portfolio Logo"
                className="h-8 sm:h-9 md:h-10 lg:h-11 transition-transform duration-300 group-hover:scale-105"
              />
              <span className="text-white text-[18px] xs:text-[12px] sm:text-base md:text-lg lg:text-xl font-semibold tracking-wide drop-shadow-[0_0_5px_rgba(0,255,255,0.6)] group-hover:drop-shadow-[0_0_8px_rgba(0,255,255,0.9)] transition-all">
                Portfolio
              </span>
            </Link>
          </div>
        </div>


        {/* Right side: Profile / Login + Hamburger */}
        <div className="flex-1 flex items-center space-x-2 sm:space-x-3 rtl:space-x-reverse md:order-2 md:flex-1 justify-end">
          <a
            href={process.env.REACT_APP_CALL_LINK || "#"}
            target="_blank"
            rel="noreferrer"
            className="hidden md:flex whitespace-nowrap bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-medium
            px-2 py-1.5 text-[9px]
            sm:px-3 sm:py-1.5 sm:text-xs 
            md:px-4 md:py-2 md:text-sm
            rounded-xl shadow-md transition-all duration-300 hover:shadow-cyan-500/20 hover:scale-[1.03] items-center gap-1 sm:gap-1.5 cursor-pointer"
          >
            <Calendar className="w-3 h-3 md:w-4 md:h-4 text-cyan-200" />
            <span>Schedule a Call</span>
          </a>

          {!isLoggedIn && (
            <Link
              to="/login"
              className="hidden md:block whitespace-nowrap backdrop-blur-md bg-blue/10 border border-white/20 text-white font-normal
              px-2 py-1 text-[9px] w-22 
              sm:px-3 sm:py-1 sm:text-xs sm:w-28 
              md:px-6 md:py-2 md:text-base md:w-40
              rounded-2xl shadow-lg hover:bg-white/20 hover:text-blue-400 transition"
            >
              Login/Sign Up{"->"}
            </Link>
          )}

          {isLoggedIn && (
            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                className="flex text-sm bg-gray-800 rounded-full focus:ring-4 focus:ring-gray-300 dark:focus:ring-gray-600"
                onClick={() => setShowDropdown((prev) => !prev)}
              >
                <span className="sr-only">Open user menu</span>
                <img
                  className="w-12 h-12 rounded-full border-2 border-black object-cover"
                  src={ProfileImage || "https://via.placeholder.com/150"}
                  alt="user"
                />
              </button>
              {showDropdown && (
                <div className="z-50 bg-black border border-white/20 rounded-2xl shadow-lg absolute top-12 right-0 w-48 text-base list-none divide-y divide-gray-100 dark:divide-gray-600">
                  <div className="px-4 py-3">
                    <span className="block text-sm text-gray-900 dark:text-white">
                      {username}
                    </span>
                    <span className="block text-sm text-gray-500 truncate dark:text-gray-400">
                      {email}
                    </span>
                  </div>
                  <ul className="py-2">
                    <li>
                      <Link
                        to="/profile"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-600 dark:hover:text-white"
                      >
                        Profile
                      </Link>
                    </li>

                    <li>
                      <Link
                        onClick={logout}
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-600 dark:hover:text-white"
                      >
                        Sign out
                      </Link>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Hamburger Menu Toggle */}
          <button
            type="button"
            className="inline-flex items-center justify-center 
            p-0.5 w-6 h-6 text-[8px] 
            xs:p-1 xs:w-7 xs:h-7 xs:text-[10px] 
            sm:p-2 sm:w-8 sm:h-8 sm:text-xs 
            md:w-9 md:h-9 md:text-sm 
            lg:w-10 lg:h-10 lg:text-base 
            xl:w-11 xl:h-11 xl:text-lg
            text-gray-500 rounded-lg md:hidden hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200 dark:text-gray-400 dark:hover:bg-gray-700 dark:focus:ring-gray-600"
            aria-expanded={showMobileMenu}
            onClick={() => setShowMobileMenu((prev) => !prev)}
          >
            <span className="sr-only">Open main menu</span>
            <svg
              className="w-3 h-3 xs:w-3.5 xs:h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 lg:w-6 lg:h-6 xl:w-7 xl:h-7"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 17 14"
            >
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M1 1h15M1 7h15M1 13h15"
              />
            </svg>
          </button>
        </div>

        {/* Navigation Links with Slide Animation */}
        <div
          className={`overflow-hidden transition-all duration-500 ease-in-out md:flex md:w-auto md:order-1 w-full ${
            showMobileMenu
              ? "max-h-[500px] opacity-100"
              : "max-h-0 opacity-100 md:max-h-full"
          }`}
        >
          <ul
            className="
    backdrop-blur-md bg-white/10 border border-white/20 rounded-2xl shadow-lg
    px-3 py-2                   /* Smaller padding for sm screens */
    flex flex-col md:flex-row items-center
    space-y-4 md:space-y-0      /* Vertical spacing on mobile, none on desktop */
    md:space-x-2 sm:space-x-0   /* Horizontal spacing on md, none on sm */
    mt-1 md:mt-0 sm:mt-2        /* Margin top adjustments */
    sm:w-full sm:p-0 sm:text-sm                   /* Full width on small screens */
    sm:px-0 sm:py-2           /* Increase padding slightly for sm screens */
    sm:space-y-2                /* Slightly reduce space between items on sm screens */
  "
          >
            {[
              { name: "Home", path: "/" },
              { name: "Experience", path: "/experience" },
              { name: "Services", path: "/services" },
              { name: "Blogs", path: "/blog" },
              { name: "Contact", path: "/contact" },
            ].map((item) => (
              <li key={item.name} className="w-full md:w-auto text-center">
                <Link
                  to={item.path}
                  onClick={() => setShowMobileMenu(false)}
                  className={`block px-2.5 rounded text-sm sm:px-2 md:px-3 md:bg-transparent md:p-0 ${
                    (item.path === "/" && location.pathname === "/" && !location.hash) ||
                    (item.path !== "/" && (location.pathname === item.path || (item.path.includes("#") && location.hash === item.path.substring(item.path.indexOf("#")))))
                      ? "text-gray-900 bg-blue-700 md:text-blue-700"
                      : "text-white hover:bg-gray-100 md:hover:text-blue-700  dark:hover:bg-gray-700"
                  }`}
                >
                  {item.name}
                </Link>
              </li>
            ))}

            {/* Mobile Only: Schedule a Call */}
            <li className="md:hidden w-full px-2.5 pt-4 border-t border-white/10 mt-2">
              <a
                href={process.env.REACT_APP_CALL_LINK || "#"}
                target="_blank"
                rel="noreferrer"
                className="w-full justify-center bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-medium px-4 py-2 text-xs rounded-xl shadow-md transition-all flex items-center gap-1.5 cursor-pointer"
                onClick={() => setShowMobileMenu(false)}
              >
                <Calendar className="w-3.5 h-3.5 text-cyan-200" />
                <span>Schedule a Call</span>
              </a>
            </li>

            {/* Mobile Only: Login / Sign Up */}
            {!isLoggedIn && (
              <li className="md:hidden w-full px-2.5 pb-2 mt-2">
                <Link
                  to="/login"
                  className="w-full text-center block backdrop-blur-md bg-blue/10 border border-white/20 text-white font-normal px-4 py-2 text-xs rounded-xl shadow-lg hover:bg-cyan/20 hover:text-blue-400 transition"
                  onClick={() => setShowMobileMenu(false)}
                >
                  Login/Sign Up {"->"}
                </Link>
              </li>
            )}
          </ul>
        </div>
      </div>
    </nav>
  );
}
